import { CollectionViewer, SelectionChange, DataSource } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, merge, Observable, Subscription, Subject, from } from 'rxjs';
import { concatMap, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { ChapterType, PageType, IBook, IPage, IChapter, IVirtualChapter, IVirtualSubChapter, PageStatus } from '../../../../models/admin';
import { AdminService } from '../../../../services/admin.services';
import { LoaderService } from '../../../../services/loader.service';
import { Store } from '@ngrx/store'
import { selectEditorBooks, selectEditorChapters, selectEditorCurrentBookId, selectEditorCurrentChapterId, selectEditorCurrentPageId, selectEditorCurrentParentChapterId, selectEditorPages } from '../../../../store/admin/admin.reducer';
import { GetEditorBooksAction, GetEditorChaptersAction, GetEditorPagesAction, AddPageAction, ReleaseBookAction, AddPageSuccessAction, DeletePageSuccessAction, GetEditorChaptersSuccessAction, ReorderBookSuccessAction, ReorderChapterSuccessAction, ReorderPageSuccessAction, DeletePageAction, ReorderTopicSuccessAction, UpdatePageAction } from '../../../../store/admin/admin.actions';
import { Actions, ofType } from '@ngrx/effects'
import { Router } from '@angular/router'
import { defaultMobileLayout, defaultTabletLayout, defaultWebLayout } from '../../books/chapters/pages/page-list/delfaultPageLayouts';
import { CdkDragDrop, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { SnackBarService } from '../../../../services/snackbar.service';
import { ChangePageTypeDialogComponent } from '../../../core/page-card/change-page-type-dialog/change-page-type-dialog.component';
import { MatDialog } from '@angular/material/dialog';

/** Flat node with expandable and level information */
export class DynamicFlatNode {
  public parentNode: DynamicFlatNode | null;
  public item: string;
  public icon: string | null;
  public route: string | null;
  public id: string;
  public type: ChapterType | null;
  public pageType: PageType | string | null;
  public data: string | null;
  public level: number;
  public expandable: boolean;
  public pageTypeColor: string;
  public isLoading: boolean;
  public children: DynamicFlatNode[];
  public isExpanded: boolean;
  public status: string;
  public isActive: boolean;
  public settings?: IVirtualChapter | null;
  get chapterType(): string | null {
    var chapterType: string | null = null;
    if (this.type) {
      const type: number = this.type
      const name = ChapterType[type];
      chapterType = name.replace(/\d+$/, '')
    }
    return chapterType
  }

  constructor(init: Partial<DynamicFlatNode> & { id: string, item: string }) {
    this.parentNode = init.parentNode ?? null;
    this.item = init.item;
    this.icon = init.icon ?? null;
    this.route = init.route ?? null;
    this.id = init.id;
    this.type = init.type ?? null;
    this.pageType = init.pageType ?? null;
    this.data = init.data ?? null;
    this.level = init.level ?? 1;
    this.expandable = init.expandable ?? false;
    this.pageTypeColor = init.pageTypeColor ?? "transparent";
    this.isLoading = init.isLoading ?? false;
    this.children = init.children ?? [];
    this.isExpanded = init.isExpanded ?? false;
    this.isActive = init.isActive ?? false;
    this.status = init.status ?? "";
    this.settings = init.settings ?? null;
    this.children?.forEach(node => {
      node.parentNode = this;
    });
  }
}

export class Chapter {
  route: string;
  chapterId: string;
  Type: string;
  title: string;
  description: string;
  songUrl: string | null;
  thumnailUrl: string | null;

  constructor(data: Partial<Chapter>) {
    this.route = data.route || "";
    this.chapterId = data.chapterId || "";
    this.Type = data.Type || "";
    this.title = data.title || "";
    this.description = data.description || "";
    this.songUrl = data.songUrl || null;
    this.thumnailUrl = data.thumnailUrl || null;
  }
}

class AboutTopic extends Chapter {
  subTopic: Chapter;

  constructor(data: Partial<AboutTopic>) {
    super(data);
    this.subTopic = new Chapter(data.subTopic || {});
  }
}

class Book {
  bookId: string;
  title: string;
  audioUrl: string | null;
  description: string | null;
  preface: Chapter;
  story1: Chapter;
  story2: Chapter;
  story3: Chapter;
  story4: Chapter;
  chat: Chapter;
  aboutTopic1: AboutTopic;
  aboutTopic2: AboutTopic;
  aboutTopic3: AboutTopic;
  aboutTopic4: AboutTopic;
  aboutTopic5: AboutTopic;

  constructor(data: Partial<Book>) {
    this.bookId = data.bookId || "";
    this.title = data.title || "";
    this.audioUrl = data.audioUrl || null;
    this.description = data.description || null;
    this.preface = new Chapter(data.preface || {});
    this.story1 = new Chapter(data.story1 || {});
    this.story2 = new Chapter(data.story2 || {});
    this.story3 = new Chapter(data.story3 || {});
    this.story4 = new Chapter(data.story4 || {});
    this.chat = new Chapter(data.chat || {});
    this.aboutTopic1 = new AboutTopic(data.aboutTopic1 || {});
    this.aboutTopic2 = new AboutTopic(data.aboutTopic2 || {});
    this.aboutTopic3 = new AboutTopic(data.aboutTopic3 || {});
    this.aboutTopic4 = new AboutTopic(data.aboutTopic4 || {});
    this.aboutTopic5 = new AboutTopic(data.aboutTopic5 || {});
  }
}

export class DynamicDataSource implements DataSource<DynamicFlatNode> {
  colors: string[] = [
    "#E4FF2B", // Light Beige (Elegant & Professional) Preface
    "#9AFE7E", // Soft Sky Blue (Modern & Corporate) Story
    "#FAD9F7", // Pale Mint Green (Calm & Fresh) Poem
    "#8EFDFE", // Soft Gold (Premium Look) Quiz
    "#FADBD8", // Light Rose (Subtle & Elegant) About
    "#E5E8E8", // Light Cool Gray (Neutral & Versatile) VisualSeries
    "#FFF3CD"  // Pale Yellow (Warm & Friendly) Chat
  ];

  dataChange = new BehaviorSubject<DynamicFlatNode[]>([]);

  // Rebuild the Virtual Chapters From Settings JSON
  public rebuildChildrenFromSettings(parentNode: DynamicFlatNode): void {
    if (!parentNode.settings) {
      parentNode.children = [];
      return;
    }

    parentNode.children = this.buildChildrenFromSettings(
      parentNode.settings,
      parentNode.level
    );

    // Safety: ensure parent references
    parentNode.children.forEach(child => {
      child.parentNode = parentNode;
    });

    // Optional but recommended:
    if (parentNode.children.length === 0 && parentNode.isExpanded) {
      parentNode.isExpanded = false;
      this.treeControl.collapse(parentNode);
    }

    this.addOrRemoveNodes(parentNode);
  }

  // Caching the Chapters and Pages
  chaptersCache = new Map<string, IChapter>();
  private pagesCache = new Map<string, IPage>();
  bookId: string | null = null;
  private _subscriptions: Subscription[] = []
  books$: Observable<IBook[]>
  chapters$: Observable<[string, IChapter[]]>
  pages$: Observable<[string, IPage[]]>;
  currentBookId$: Observable<string>;
  currentChapterId$: Observable<string>;
  currentParentChapterId$: Observable<string>;
  currentPageId$: Observable<string>;
  private _activeNode: DynamicFlatNode | null = null;

  // Check If A Node Has Settings Or Not
  hasSettings(node: DynamicFlatNode): boolean {
    // Check if this is a chapter node (not a book or page)
    if (node.expandable && node.type !== 0 && !node.pageType) {
      return this.checkNodeSettings(node);
    }
    return false;
  }

  // Check For the Node Settings
  private checkNodeSettings(node: DynamicFlatNode): boolean {
    let hasSettings = false;
    this.chapters$.pipe(take(1)).subscribe(([parentId, chapters]) => {
      const chapter = chapters.find(c => c.ChapterId === node.id);
      hasSettings = !!(chapter?.Settings);
    });
    return hasSettings;
  }

  // Method to get the settings for a node
  getNodeSettings(node: DynamicFlatNode): IVirtualChapter | null {
    let settings: IVirtualChapter | null = null;
    this.chapters$.pipe(take(1)).subscribe(([parentId, chapters]) => {
      const chapter = chapters.find(c => c.ChapterId === node.id);
      settings = chapter?.Settings || null;
    });
    return settings;
  }

  get activeNode(): DynamicFlatNode | null {
    return this._activeNode;
  }
  set activeNode(value: DynamicFlatNode | null) {
    if (value != this._activeNode) {
      this.changeActiveStatus(this._activeNode, false);
      this._activeNode = value;
      this.changeActiveStatus(this._activeNode, true);
    }
  }
  get data(): DynamicFlatNode[] {
    return this.dataChange.value;
  }
  set data(value: DynamicFlatNode[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  // Cache the Chapters and Pages To Avoid Calling Server when expanding nodes
  private initializeCaches(): void {
    this._subscriptions.push(
      this.chapters$.subscribe(([_, chapters]) => {
        if (chapters) {
          chapters.forEach(chapter => {
            // Cache the chapter itself
            this.chaptersCache.set(chapter.ChapterId, chapter);

            // Cache all subchapters
            if (chapter.SubChapters && chapter.SubChapters.length > 0) {
              chapter.SubChapters.forEach(subChapter => {
                this.chaptersCache.set(subChapter.ChapterId, subChapter);
              });
            }
          });
        }
      })
    );

    // Cache pages - FIXED: Update cache immediately when pages arrive
    this._subscriptions.push(
      this.pages$.subscribe(([chapterId, pages]) => {
        if (pages && pages.length > 0) {
          pages.forEach(page => {
            this.pagesCache.set(page.PageId, page);
          });
        }
      })
    );
  }

  constructor(
    private treeControl: FlatTreeControl<DynamicFlatNode>,
    private store: Store,
  ) {
    // _database.nodeDataChange.subscribe({
    //   next: (node) => {
    //     if(node && node.isExpanded)
    //       this.addOrRemoveNodes(node);
    //   }
    // })
    this.books$ = this.store.select(selectEditorBooks) as Observable<IBook[]>;
    this.chapters$ = this.store.select(selectEditorChapters) as Observable<[string, IChapter[]]>;
    this.pages$ = this.store.select(selectEditorPages) as Observable<[string, IPage[]]>;
    this.currentBookId$ = this.store.select(selectEditorCurrentBookId) as Observable<string>;
    this.currentChapterId$ = this.store.select(selectEditorCurrentChapterId) as Observable<string>;
    this.currentParentChapterId$ = this.store.select(selectEditorCurrentParentChapterId) as Observable<string>;
    this.currentPageId$ = this.store.select(selectEditorCurrentPageId) as Observable<string>;

    // Initialize the Cache
    this.initializeCaches();
  }
  changeActiveStatus(node: DynamicFlatNode | null, isActive: boolean) {
    if (node) {
      node.isActive = isActive
      this.changeActiveStatus(node.parentNode, isActive)
    }
  }

  getPageType(type: PageType): string {
    return PageType[type]
  }

  // Get Chapter Titile From Chapters Cache
  private getChapterTitleById(chapterId: string): string {
    const chapter = this.chaptersCache.get(chapterId);
    return chapter && chapter.Title ? chapter.Title : 'Mapped Chapter';
  }

  // Get Page From Page Cache
  private getPageById(pageId: string): IPage | null {
    return this.pagesCache.get(pageId) || null;
  }

  // Build the Subchapters and Pages From Chapters Settings string
  buildChildrenFromSettings(
    settings: IVirtualChapter,
    level: number
  ): DynamicFlatNode[] {
    const children: DynamicFlatNode[] = [];

    settings.SubChapters.forEach((subChapter) => {
      const isMappedChapter = !!subChapter.MappedChapterId;
      if (isMappedChapter) {
        const chapterTitle = this.getChapterTitleById(subChapter.MappedChapterId!);
        children.push(new DynamicFlatNode({
          item: chapterTitle,
          id: subChapter.MappedChapterId!,
          level: level + 1,
          type: subChapter.Type,
          expandable: true,
          children: []
        }));
      } else {
        subChapter.Pages.forEach((pageId, pageIndexInSubChapter) => {
          const page = this.getPageById(pageId);
          const displayIndex = subChapter.startPageIndex + pageIndexInSubChapter + 1;

          // Use subChapter.Type which is the page type for this group
          const pageTypeValue = page?.Type ?? subChapter.Type;
          const pageTypeString = this.getPageType(pageTypeValue);
          const pageColor = this.colors[pageTypeValue] || "transparent";

          children.push(new DynamicFlatNode({
            item: page?.Title
              ? `P${displayIndex}-${page.Title}`
              : `Page ${displayIndex}`,
            id: pageId,
            pageType: pageTypeString,
            pageTypeColor: pageColor,
            level: level + 1,
            expandable: false,
            status: page?.Status,
            children: []
          }));
        });
      }
    });
    return children;
  }

  connect(collectionViewer: CollectionViewer): Observable<DynamicFlatNode[]> {
    // Dispatch the "GetEditorBooksAction" to trigger the subscription of books,chapters,pages, etc.
    // If we don't dispatch the action, then tree view component shows empty when
    // on different route than "/admin/books"
    this.store.dispatch(GetEditorBooksAction())
    this._subscriptions.push(
      this.books$.subscribe(books => {
        if (books) {
          setTimeout(() => {
            this.data = books.map(book => new DynamicFlatNode(
              {
                item: book.Title ? book.Title : "empty book name",
                id: book.BookId,
                level: 0,
                type: 0,
                status: (book as any).Status,
                expandable: true
              }));
          }, 100);
        }
      }),
      this.currentBookId$.subscribe(currentBookId => {
        if (currentBookId) {
          this.bookId = currentBookId;
          let node = this.findNodeById(null, currentBookId)
          if (node) {
            this.activeNode = node
            node.isExpanded = true;
            if (node)
              this.treeControl.expand(node);
          }
        }
      }),
      this.currentChapterId$.subscribe(currentChapterId => {
        if (currentChapterId) {
          let node = this.findNodeById(null, currentChapterId)
          if (node) {
            this.activeNode = node
            node.isExpanded = true;
            if (node)
              this.treeControl.expand(node);
          }
        }
      }),
      this.currentParentChapterId$.subscribe(currentParentChapterId => {
        if (currentParentChapterId) {
          let node = this.findNodeById(null, currentParentChapterId)
          if (node) {
            this.activeNode = node
            node.isExpanded = true;
            if (node)
              this.treeControl.expand(node);
          }
        }
      }),
      this.currentPageId$.subscribe(currentPageId => {
        if (currentPageId) {
          let node = this.findNodeById(null, currentPageId)
          if (node) {
            this.activeNode = node
          }
        }
      }),
      this.chapters$.subscribe(chaptersWithParent => {
        if (chaptersWithParent && chaptersWithParent[1]) {
          let parentNode = this.findNodeById(null, chaptersWithParent[0])
          if (parentNode) {
            if (parentNode.level == this.activeNode?.level) {
              parentNode.children.splice(0, parentNode.children.length)
              let nodes = chaptersWithParent[1].map(chapter => {
                // Check if chapter has Settings with SubChapters
                if (chapter.Settings && chapter.Settings.SubChapters && chapter.Settings.SubChapters.length > 0) {
                  // Build children from Settings.SubChapters instead of chapter.SubChapters
                  return new DynamicFlatNode({
                    item: chapter.Title ? chapter.Title : "empty chapter name",
                    id: chapter.ChapterId,
                    level: parentNode.level + 1,
                    type: chapter.Type,
                    parentNode: parentNode,
                    expandable: true,
                    settings: chapter.Settings,
                    children: [] // Empty for now, will be populated when pages load
                  });
                } else {
                  // Use default SubChapters if no Settings
                  return new DynamicFlatNode({
                    item: chapter.Title ? chapter.Title : "empty chapter name",
                    id: chapter.ChapterId,
                    level: parentNode.level + 1,
                    type: chapter.Type,
                    parentNode: parentNode,
                    expandable: true,
                    settings: chapter.Settings,
                    children: chapter.SubChapters.map(subChapter => new DynamicFlatNode({
                      item: subChapter.Title ? subChapter.Title : "empty chapter name",
                      id: subChapter.ChapterId,
                      level: parentNode.level + 2,
                      type: subChapter.Type,
                      expandable: true,
                      settings: subChapter.Settings,
                      children: []
                    }))
                  });
                }
              });
              this.addChildren(parentNode, nodes);
            }
          }
        }
      }),
      this.pages$.subscribe(pagesWithParent => {
        if (pagesWithParent && pagesWithParent[1]) {
          // Cache the pages first before building children
          pagesWithParent[1].forEach(page => {
            this.pagesCache.set(page.PageId, page);
          });
          let parentNode = this.findNodeById(null, pagesWithParent[0]);
          if (parentNode) {
            // Check if this is a virtual chapter (has settings)
            if (parentNode.settings) {
              // Build The Virtual Chapter From Settings
              const latestChapter = this.chaptersCache.get(parentNode.id);

              if (latestChapter?.Settings) {
                parentNode.settings = latestChapter.Settings;
              }

              parentNode.children = this.buildChildrenFromSettings(
                parentNode.settings,
                parentNode.level
              );

              // Set parent reference on new children
              parentNode.children.forEach(child => child.parentNode = parentNode);

              // Update the tree display
              if (parentNode.isExpanded) {
                this.addOrRemoveNodes(parentNode);
              }

              return; // Exit early for virtual chapters
            }

            // Normal behavior for chapters without settings
            if (parentNode.level == this.activeNode?.level) {
              const nodesToAdd = pagesWithParent[1].filter(p => p.chapterId === parentNode.id);

              // Keep only topic/subchapter children (non-page), pages will be re-inserted
              const topicChildren = parentNode.children.filter(t => !t.pageType);

              const pageNodes = nodesToAdd.map((p, i) => new DynamicFlatNode({
                item: (p.Title) ? `P${String(i + 1)}-${p.Title}` : `Page ${String(i + 1)}`,
                id: p.PageId,
                pageType: this.getPageType(p.Type),
                pageTypeColor: this.colors[p.Type],
                level: parentNode.level + 1,
                parentNode: parentNode,
                status: p.Status,
                expandable: false
              }));

              // First build the pages then the topics
              parentNode.children = [...pageNodes, ...topicChildren];
              this.addOrRemoveNodes(parentNode);
            }
          }
        }
      }),
    )

    this.treeControl.expansionModel.changed.subscribe(change => {
      if (
        (change as SelectionChange<DynamicFlatNode>).added ||
        (change as SelectionChange<DynamicFlatNode>).removed
      ) {
        this.handleTreeControl(change as SelectionChange<DynamicFlatNode>);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }
  findNodeById(node: DynamicFlatNode | null, id: string): DynamicFlatNode | null {
    if (node) {
      if (node.id === id) return node;
      for (let child of node.children) {
        const found = this.findNodeById(child, id);
        if (found) return found;
      }
    }
    else {
      for (let child of this.data) {
        const found = this.findNodeById(child, id);
        if (found) return found;
      }
    }
    return null;
  }
  disconnect(collectionViewer: CollectionViewer): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

  addChildren(node: DynamicFlatNode, nodes: DynamicFlatNode[]) {
    if (nodes.length > 0) {
      node.children.splice(node.children.length, 0, ...nodes);
      this.addOrRemoveNodes(node);
    }
  }
  /** Handle expand/collapse behaviors */
  handleTreeControl(change: SelectionChange<DynamicFlatNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed
        .slice()
        .reverse()
        .forEach(node => this.toggleNode(node, false));
    }
  }



  addOrRemoveNodes(node: DynamicFlatNode) {
    if (!node)
      return;
    const index = this.data.indexOf(node);
    if (!node.children || index < 0) {
      return;
    }
    let count = 0;
    for (
      let i = index + 1;
      i < this.data.length && this.data[i].level > node.level;
      i++, count++
    ) { }
    this.data.splice(index + 1, count);
    if (node.isExpanded) {
      node.isExpanded = true;
      this.data.splice(index + 1, 0, ...node.children);
    } else {
      node.isExpanded = false;
    }

    // notify the change
    this.dataChange.next(this.data);

  }


  toggleNode(node: DynamicFlatNode, expand: boolean) {
    //node.isLoading.set(true);
    if (expand) {
      node.isExpanded = true;
      if (node.level == 0) {
        this.store.dispatch(GetEditorChaptersAction({ bookId: node.id }));
      }
      else if (node.level > 0 && node.expandable) {
        this.store.dispatch(GetEditorPagesAction({ chapterId: node.id }));
      }
    }
    else {
      node.isExpanded = false
      this.addOrRemoveNodes(node)
    }
  }
}

/**
 * @title Tree with dynamic data
 */
@Component({
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrl: './tree.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeComponent implements OnInit, OnDestroy {
  @Input() permissions: string[] = []; // Accepts string array input
  private _componentSubscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();
  constructor(
    private router: Router,
    private adminService: AdminService,
    private loaderService: LoaderService,
    private store: Store,
    private actions$: Actions,
    private snackBarService: SnackBarService,
    private dialog: MatDialog,
  ) {
    this.treeControl = new FlatTreeControl<DynamicFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new DynamicDataSource(this.treeControl, store);

    this.dataSource.data = [];// this.database.initialData();
  }

  PageType = PageType;
  pageTypes: string[] = Object.values(PageType).filter(value => typeof value === 'string');

  draggingNode: any = null;
  activeMenu: HTMLElement | null = null;
  treeControl: FlatTreeControl<DynamicFlatNode>;
  dataSource: DynamicDataSource;
  clickedNode?: DynamicFlatNode;

  contextMenuMap = new Map<number, string[]>([
    [-1, ['Add Book']],
    [0, ['Add Story', 'Add Visual']],
    [1, ['Add Page']],
    [2, ['Add Topic', 'Add Page']],
  ]);
  typeMap = new Map<string, number>([
    ['Book', 0],
    ['Story', 1],
    ['Visual', 2],
    ['Topic', 3]
  ]);

  getLevel = (node: DynamicFlatNode) => node.level;

  isExpandable = (node: DynamicFlatNode) => node.expandable;

  ischapter = (_: number, _nodeData: DynamicFlatNode) => _nodeData.expandable && _nodeData.type !== 0;

  isbook = (_: number, _nodeData: DynamicFlatNode) => _nodeData.type === 0;
  toggleChildren(node: any) {
    node.isExpanded = !node.isExpanded;
  }
  deletePage(node: DynamicFlatNode | undefined) {
    if (!node) {
      return;
    }
    if (node.parentNode) {
      if (confirm('Are you sure you want to delete this page?')) {
        this.store.dispatch(DeletePageAction({
          chapterId: node.parentNode.id,
          pageId: node.id
        }));
      }
    }

    this.clickedNode = undefined;
  }
  addPage(pt: string) {
    if (this.dataSource.activeNode) {
      const pageEnum: PageType | undefined = PageType[pt as keyof typeof PageType];
      let mobileLayout = defaultMobileLayout;
      let webLayout = defaultWebLayout;
      let tabletLayout = defaultTabletLayout;
      let page: IPage = {
        Type: pageEnum,
        PageId: '',
        Title: '',
        PageType: PageType.Story,
        Status: PageStatus.Draft,
        AudioUrl: '',
        AccessRights: [],
        MobileLayout: mobileLayout,
        WebLayout: webLayout,
        TabletLayout: tabletLayout,
        CreatedAt: '',
        UpdatedAt: '',
        isLoading: true,
        chapterId: this.dataSource.activeNode.id,
        RPAllowed: false,
        Spellings: null
      };

      this.store.dispatch(AddPageAction({
        chapterId: this.dataSource.activeNode.id,
        page
      }));
    }
  }

  changePageType(node: DynamicFlatNode | undefined): void {
    if (!node) {
      return;
    }
    if (node.status != PageStatus.Draft) {
      this.snackBarService.showSnackBar('error', "Page can only be updated in Draft state.");
      return;
    }

    const dialogRef = this.dialog.open(ChangePageTypeDialogComponent, {
      width: '420px',
      data: {
        currentPageType: PageType[node.pageType as keyof typeof PageType]
      }
    });

    dialogRef.afterClosed().subscribe((pageType: PageType | undefined) => {
      if (!pageType) {
        return;
      }

      // Save/update page type
      if (node.parentNode) {
        this.updatePageData({ PageId: node.id, chapterId: node.parentNode.id, Type: pageType })
      }
    });

    this.clickedNode = undefined;
  }

  updatePageData(page: Partial<IPage> & {
    PageId: string;
    chapterId: string;
  }) {
    if (page) {
      this.store.dispatch(
        UpdatePageAction({
          page
        }),
      );
    }
  }


  ngOnInit(): void {
    // Handle Page Added/Deletion Success Action
    this._componentSubscriptions.push(
      this.actions$.pipe(
        ofType(AddPageSuccessAction, DeletePageSuccessAction),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.dataSource.bookId) {
          this.store.dispatch(GetEditorChaptersAction({ bookId: this.dataSource.bookId }));
        }
      })
    );

    // Handle Chapter Fetch Success Action
    this._componentSubscriptions.push(
      this.actions$.pipe(
        ofType(GetEditorChaptersSuccessAction),
        takeUntil(this.destroy$)
      ).subscribe((response) => {
        //  Fetch the Pages for all chapter types (which will rebuild the tree for Visual Series Also)
        if (this.dataSource.activeNode && this.dataSource.activeNode.type) {
          this.store.dispatch(GetEditorPagesAction({ chapterId: this.dataSource.activeNode.id }));
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._componentSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  getType(type: string) {
    return this.typeMap.get(type);
  }
  setActiveNode(event: MouseEvent, node: DynamicFlatNode) {
    event.stopPropagation();
    this.dataSource.activeNode = node
  }
  getBookNode(node: DynamicFlatNode): DynamicFlatNode | null {
    if (node.parentNode && node.parentNode.type)
      return this.getBookNode(node.parentNode);
    else if (node.parentNode && node.parentNode.type == 0)
      return node.parentNode;
    else if (node.parentNode && node.type == 0)
      return node;
    else
      return null;
  }
  openNode(event: MouseEvent, node: DynamicFlatNode) {
    event.stopPropagation();
    this.dataSource.activeNode = node
    if (this.dataSource.activeNode.level == 0)
      this.router.navigate([`/admin/books/${this.dataSource.activeNode?.id}/chapters`])
    else if (this.dataSource.activeNode.level == 1) {
      if (this.dataSource.activeNode.type && this.dataSource.activeNode.type > 0)
        this.router.navigate([`/admin/books/${this.dataSource.activeNode?.parentNode?.id}/chapters/${this.dataSource.activeNode?.id}/subchapters`])
      else
        this.router.navigate([`admin/books/${this.dataSource.activeNode?.parentNode?.id}/pages/${this.dataSource.activeNode?.id}`])
    }
    else if (this.dataSource.activeNode.level == 2) {
      if (this.dataSource.activeNode.type && this.dataSource.activeNode.type > 0)
        this.router.navigate([`admin/books/${this.dataSource.activeNode?.parentNode?.parentNode?.id}/chapters/${this.dataSource.activeNode?.parentNode?.id}/subchapters/${this.dataSource.activeNode?.id}/pages`])
      else
        this.router.navigate([`admin/books/${this.dataSource.activeNode?.parentNode?.parentNode?.id}/chapters/${this.dataSource.activeNode?.parentNode?.id}/pages/${this.dataSource.activeNode?.id}`])
    }//admin/books/8932e01e-c59a-49fc-8033-02d53664fce6/chapters/4f48b968-38e2-4f58-aff4-a8a0d5ade5e0/subchapters/d4e52c33-1d3d-478b-9d01-507a2ad16db9/pages
    else if (this.dataSource.activeNode.level == 3) {
      if (this.dataSource.activeNode.type && this.dataSource.activeNode.type > 0)
        this.router.navigate([`/admin/books/${this.dataSource.activeNode?.parentNode?.parentNode?.id}/chapters/${this.dataSource.activeNode?.parentNode?.id}/subchapters`, this.dataSource.activeNode?.id])
      else
        this.router.navigate([`admin/books/${this.dataSource.activeNode?.parentNode?.parentNode?.parentNode?.id}/chapters/${this.dataSource.activeNode?.parentNode?.parentNode?.id}/subchapters/${this.dataSource.activeNode?.parentNode?.id}/pages/${this.dataSource.activeNode?.id}`])
    }
  }

  // Helper to convert PageType string back to enum
  private getPageTypeEnum(pageTypeString: string): number {
    return PageType[pageTypeString as keyof typeof PageType] as number;
  }

  // Rebuild the Settings JSON from parent node Of Vitual Chapter
  private rebuildSettingsFromChildren(parentNode: DynamicFlatNode): IVirtualChapter {
    const settings: IVirtualChapter = {
      ChapterId: parentNode.id,
      TotalPages: 0,
      SubChapters: []
    };

    let currentSubChapter: IVirtualSubChapter | null = null;
    let currentPageIndex = 0;

    // Read from children — caller is responsible for syncing this before calling
    for (const child of parentNode.children) {
      const isMappedChapter = child.expandable &&
        child.type !== null &&
        child.type !== undefined &&
        !child.pageType;

      const isPage = !child.expandable &&
        child.pageType !== null &&
        child.pageType !== undefined;

      if (isMappedChapter) {
        if (currentSubChapter) {
          settings.SubChapters.push(currentSubChapter);
          currentSubChapter = null;
        }
        settings.SubChapters.push({
          ChapterId: parentNode.id,
          MappedChapterId: child.id,
          Type: child.type!,
          startPageIndex: 0,
          Pages: []
        });
      } else if (isPage) {
        if (!currentSubChapter) {
          currentSubChapter = {
            ChapterId: parentNode.id,
            Type: 100,
            startPageIndex: currentPageIndex,
            Pages: []
          };
        }
        currentSubChapter.Pages.push(child.id);
        currentPageIndex++;
      }
    }

    if (currentSubChapter) {
      settings.SubChapters.push(currentSubChapter);
    }

    settings.TotalPages = currentPageIndex;

    // Sync subchapters, add/remove subchapters from settings.SubChapters array based on actual Subchapters data
    // ------------------------------------
    const mainSubchapters = this.dataSource.chaptersCache.get(settings.ChapterId)?.SubChapters;
    if (mainSubchapters) {
      const settingsSubchapters = settings.SubChapters;

      // Create a set of valid IDs from main array
      const mainIds = new Set(mainSubchapters?.map(item => item.ChapterId));

      // STEP 1: Remove duplicate chapters, if any
      // -------------------------------
      const uniqueSettingsSubchapters: IVirtualSubChapter[] = [];
      const seenIds = new Set();

      for (const item of settingsSubchapters) {
        const id = item.MappedChapterId;
        // except type 100, it can be duplicate type
        if (item.Type == 100 || !seenIds.has(id)) {
          seenIds.add(id);
          uniqueSettingsSubchapters.push(item);
        }
      }

      // Step 2: Remove extra subchapters which does not belong to this chapter but still present in settings.SubChapters list
      const syncedSubchapters = uniqueSettingsSubchapters.filter(item =>
        mainIds.has(item.MappedChapterId!) || item.Type == 100
      );

      // Step 3: Find missing subchapters from main subchapters array
      const settingsIds = new Set(syncedSubchapters.map(item => item.MappedChapterId));
      const missingItems = mainSubchapters?.filter(item =>
        !settingsIds.has(item.ChapterId)
      );

      // insert missing subchapters
      if (missingItems.length) {
        missingItems.forEach((mainSubChapter) => {
          syncedSubchapters.push({
            ChapterId: parentNode.id,
            MappedChapterId: mainSubChapter.ChapterId,
            Type: mainSubChapter.Type,
            startPageIndex: 0,
            Pages: []
          })
        })
      }

      // Step 4: Update the subChapters list
      settings.SubChapters = [...syncedSubchapters];
    }
    return settings;
  }

  onDragStarted(event: CdkDragStart, node: DynamicFlatNode): void {
    // collapse the node, if expanded
    if(node.isExpanded) {
      this.treeControl.collapse(node);
    }
  }

  drop(event: CdkDragDrop<DynamicFlatNode[]>) {
    const sourceNode = this.dataSource.data[event.previousIndex];
    let targetNode = this.dataSource.data[event.currentIndex];

    // when targetNode and sourceNode has different level, i.e. subchapter drop from one visual series to another visual series chapter
    if (targetNode && sourceNode && targetNode.level < sourceNode.level && event.currentIndex < event.previousIndex) {
      targetNode = this.dataSource.data[event.currentIndex - 1];
      if (!targetNode) {
        return;
      }
    }

    let source = sourceNode;
    let target: DynamicFlatNode | null =
      (targetNode.level < source.level) ? null : targetNode;

    let sourceParent = source.parentNode;
    let targetParent = target?.parentNode ??
      (targetNode.level < source.level ? targetNode : targetNode.parentNode);

    // Early destNode calculation
    let destNode: DynamicFlatNode | null = target;

    if (target && event.currentIndex < event.previousIndex) {
      if (event.currentIndex > 0 &&
        this.dataSource.data[event.currentIndex - 1].level === target.level) {
        destNode = this.dataSource.data[event.currentIndex - 1];
      } else {
        destNode = null;
      }
    }

    // cross move operations are only allowed for visual series topic and destNode should be null in that case
    if (sourceParent?.id != targetParent?.id) {
      destNode = null;
    }

    const isSameParentVisual = sourceParent === targetParent &&
      sourceParent?.chapterType === 'VisualSeries';

    // Handle the page/topic drag within the same visual series
    if (isSameParentVisual) {
      const previousData = [...this.dataSource.data];
      const previousSettings = sourceParent.settings
        ? structuredClone(sourceParent.settings)
        : null;

      // Move in flat data
      moveItemInArray(this.dataSource.data, event.previousIndex, event.currentIndex);

      //Sync children from flat data WITH boundary guard (not slice+filter)
      const syncChildrenFromFlatData = (parent: DynamicFlatNode) => {
        const idx = this.dataSource.data.indexOf(parent);
        if (idx === -1) return;
        const synced: DynamicFlatNode[] = [];
        for (let i = idx + 1; i < this.dataSource.data.length; i++) {
          const node = this.dataSource.data[i];
          if (node.level <= parent.level) break; // ← boundary guard
          if (node.level === parent.level + 1) synced.push(node);
        }
        parent.children = synced;
      };

      syncChildrenFromFlatData(sourceParent);

      // Rebuild settings from now-synced children
      const rebuiltSettings = this.rebuildSettingsFromChildren(sourceParent);

      const settingsChanged = !previousSettings ||
        JSON.stringify(rebuiltSettings) !== JSON.stringify(previousSettings);

      // Ordered page IDs from flat data
      const getOrderedPageIdsFromFlatData = (parent: DynamicFlatNode): string[] => {
        const parentIndex = this.dataSource.data.indexOf(parent);
        if (parentIndex === -1) return [];
        const pageIds: string[] = [];
        for (let i = parentIndex + 1; i < this.dataSource.data.length; i++) {
          const node = this.dataSource.data[i];
          if (node.level <= parent.level) break;
          if (node.level === parent.level + 1 && node.pageType) {
            pageIds.push(node.id);
          }
        }
        return pageIds;
      };

      const getPrevPageId = (): string | null => {
        const parentIndex = this.dataSource.data.indexOf(sourceParent!);
        if (parentIndex === -1) return null;
        let prevPageId: string | null = null;
        for (let i = parentIndex + 1; i < this.dataSource.data.length; i++) {
          const node = this.dataSource.data[i];
          if (node.level <= sourceParent?.level) break;
          if (node.level === sourceParent?.level + 1 && node.pageType) {
            if (node.id === source.id) break;
            prevPageId = node.id;
          }
        }
        return prevPageId;
      };

      const shouldCallPageReorder = (() => {
        if (!source.pageType) return false;
        const cachedPageOrder = Array.from(this.dataSource['pagesCache'].values())
          .filter(p => p.chapterId === sourceParent?.id)
          .map(p => p.PageId);
        const newPageOrder = getOrderedPageIdsFromFlatData(sourceParent);
        if (cachedPageOrder.length !== newPageOrder.length) return true;
        return cachedPageOrder.some((id, i) => id !== newPageOrder[i]);
      })();

      const shouldCallSettingsUpdate = settingsChanged;

      // Update the flat data display ONCE — do NOT call addOrRemoveNodes again after this
      this.dataSource.data = [...this.dataSource.data];
      this.dataSource.dataChange.next(this.dataSource.data);

      if (!shouldCallSettingsUpdate && !shouldCallPageReorder) {
        return;
      }

      this.loaderService.showLoader();

      const calls: Observable<any>[] = [];

      if (shouldCallPageReorder) {
        const prevPageId = getPrevPageId();
        const newPageOrder = getOrderedPageIdsFromFlatData(sourceParent);

        calls.push(
          this.adminService.postEditorPageReorder(source.id, prevPageId).pipe(
            tap(() => {
              const newOrderedPages = newPageOrder
                .map(id => this.dataSource['pagesCache'].get(id))
                .filter(Boolean) as IPage[];

              Array.from(this.dataSource['pagesCache'].entries())
                .filter(([_, p]) => p.chapterId === sourceParent?.id)
                .forEach(([id]) => this.dataSource['pagesCache'].delete(id));

              newOrderedPages.forEach(p => this.dataSource['pagesCache'].set(p.PageId, p));
              this.store.dispatch(ReorderPageSuccessAction());
            })
          )
        );
      }

      if (shouldCallSettingsUpdate) {
        calls.push(
          this.adminService.updateChapter(sourceParent.id, { Settings: rebuiltSettings }).pipe(
            tap(() => {
              sourceParent.settings = rebuiltSettings;
              if (this.dataSource.bookId) {
                this.store.dispatch(GetEditorChaptersAction({ bookId: this.dataSource.bookId }));
              }
            })
          )
        );
      }

      from(calls).pipe(
        concatMap(call => call),
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hideLoader())
      ).subscribe({
        error: (err) => {
          this.dataSource.data = [...previousData];
          this.dataSource.dataChange.next(this.dataSource.data);
          if (sourceParent) {
            sourceParent.settings = previousSettings;
            syncChildrenFromFlatData(sourceParent);
          }
        }
      });

      return;
    }

    const isCrossParentTopicDrop =
      source.chapterType === 'Topic' &&
      sourceParent?.chapterType === 'VisualSeries' &&
      targetParent?.chapterType === 'VisualSeries' &&
      sourceParent !== targetParent;

    if (
      (target && source.level === target.level &&
        source.chapterType !== 'Preface' && target.chapterType !== 'Preface' &&
        ((sourceParent === targetParent) ||
          (sourceParent?.chapterType === 'VisualSeries' &&
            targetParent?.chapterType === 'VisualSeries' &&
            source.chapterType === 'Topic' && target.chapterType === 'Topic')) &&
        ((source.pageType && target.pageType) || (!source.pageType && !target.pageType))
      ) ||
      (!target && sourceParent !== targetParent &&
        sourceParent?.chapterType === 'VisualSeries' &&
        targetParent?.chapterType === 'VisualSeries' &&
        source.chapterType === 'Topic') ||
      (source.pageType && target && !target.pageType &&
        target.chapterType === 'Topic' && sourceParent === targetParent) ||
      isCrossParentTopicDrop
    ) {
      const handleSuccess = () => {
        let newParent: DynamicFlatNode;

        if (this.dataSource.data[event.currentIndex].level < source.level) {
          newParent = this.dataSource.data[event.currentIndex];
        } else {
          newParent = this.dataSource.data[event.currentIndex].parentNode!;
        }

        if (event.previousIndex > event.currentIndex &&
          this.dataSource.data[event.currentIndex].chapterType === 'VisualSeries') {
          newParent = this.dataSource.data[event.currentIndex];
        }

        if (sourceParent) {
          sourceParent.children = sourceParent.children.filter(c => c.id !== source.id);
        }

        const insertIndex = destNode
          ? targetParent ? targetParent.children.findIndex(c => c.id === destNode.id) + 1 : 0
          : 0;

        targetParent?.children.splice(insertIndex, 0, source);
        source.parentNode = targetParent!;

        moveItemInArray(this.dataSource.data, event.previousIndex, event.currentIndex);

        if (sourceParent) this.dataSource.addOrRemoveNodes(sourceParent);
        if (targetParent) this.dataSource.addOrRemoveNodes(targetParent);

        this.dataSource.data = [...this.dataSource.data];

        if (targetParent && !targetParent.isExpanded) {
          this.treeControl.expand(targetParent);
          targetParent.isExpanded = true;
        }

        let current = this.dataSource.data[event.currentIndex];
        while (current?.parentNode) current = current.parentNode;
        if (current && current.status === "Published") {
          this.store.dispatch(ReleaseBookAction({ bookId: current.id }));
        }
      };

      const handleSubchaptersMoveAcrossChaptersSuccess = (sourceId?: string, targetId?: string) => {
        if (!this.dataSource.bookId) return;

        this.adminService.getEditorChaptersWithSubchapters(this.dataSource.bookId).subscribe({
          next: (response) => {
            // Refresh chaptersCache first, synchronously
            response.Value.forEach(ch => {
              this.dataSource['chaptersCache'].set(ch.ChapterId, ch);
              ch.SubChapters?.forEach(sub => {
                this.dataSource['chaptersCache'].set(sub.ChapterId, sub);
              });
            });

            // Rebuild source node directly from fresh settings
            if (sourceId) {
              const node = this.dataSource.findNodeById(null, sourceId);
              if (node) {
                const fresh = this.dataSource['chaptersCache'].get(sourceId);
                node.settings = fresh?.Settings ?? null;
                if (sourceParent && sourceParent.id === sourceId) {
                  sourceParent.settings = node.settings;
                }

                if (node.settings?.SubChapters?.length) {
                  // rebuildChildrenFromSettings builds from pagesCache + calls addOrRemoveNodes
                  this.dataSource.rebuildChildrenFromSettings(node);
                } else {
                  // All topics moved out — source is now empty, collapse it
                  node.children = [];
                  node.settings = null;
                  if (node.isExpanded) {
                    node.isExpanded = false;
                    this.treeControl.collapse(node);
                  }
                  this.dataSource.addOrRemoveNodes(node);
                }
              }
            }

            // Rebuild target node directly from fresh settings
            if (targetId) {
              const node = this.dataSource.findNodeById(null, targetId);
              if (node) {
                const fresh = this.dataSource['chaptersCache'].get(targetId);
                node.settings = fresh?.Settings ?? null;
                if (targetParent && targetParent.id === targetId) {
                  targetParent.settings = node.settings;
                }

                if (node.settings?.SubChapters?.length) {
                  this.dataSource.rebuildChildrenFromSettings(node);
                }

                // Ensure target is expanded so the moved topic is visible
                if (!node.isExpanded) {
                  node.isExpanded = true;
                  this.treeControl.expand(node);
                }
              }
            }
          },
          error: () => this.snackBarService.showSnackBar('error', 'Refresh failed')
        });
      };

      if (source.pageType) {
        this.adminService.postEditorPageReorder(source.id, destNode ? destNode.id : null)
          .pipe(takeUntil(this.destroy$), finalize(() => this.loaderService.hideLoader()))
          .subscribe({
            next: () => {
              handleSuccess();
              this.store.dispatch(ReorderPageSuccessAction());
            },
            error: (err) => console.error('Page reorder failed:', err)
          });
      } else if (source.type) {
        if (isCrossParentTopicDrop) {
          const sourceId = sourceParent?.id;
          const targetId = targetParent?.id;

          this.loaderService.showLoader();

          this.adminService.postEditorSubChapterReorderAcrossParents(
            sourceId, targetId, source.id, destNode ? destNode.id : null
          ).pipe(finalize(() => this.loaderService.hideLoader()))
            .subscribe({
              next: () => {
                handleSuccess();
                handleSubchaptersMoveAcrossChaptersSuccess(sourceId, targetId);
                this.store.dispatch(ReorderTopicSuccessAction());
              },
              error: (err) => {
                console.error('Cross move failed:', err);
              }
            });
        } else {
          this.adminService.postEditorChapterReorder(source.id, destNode ? destNode.id : null)
            .pipe(takeUntil(this.destroy$), finalize(() => this.loaderService.hideLoader()))
            .subscribe({
              next: () => {
                handleSuccess();
                this.store.dispatch(ReorderChapterSuccessAction());
              },
              error: (err) => console.error('Chapter reorder failed:', err)
            });
        }
      } else {
        this.adminService.postEditorBookReorder(source.id, destNode ? destNode.id : null)
          .pipe(takeUntil(this.destroy$), finalize(() => this.loaderService.hideLoader()))
          .subscribe({
            next: () => {
              handleSuccess();
              this.store.dispatch(ReorderBookSuccessAction());
            },
            error: (err) => console.error('Book reorder failed:', err)
          });
      }
    }
  }
}
